<template>
  <div class="sf-page">
    <div class="sf-gradient">
      <div class="sf-panel">
        <button type="button" class="sf-btn sf-btn--back" @click="goBack" aria-label="Volver">
          <i class="fa fa-chevron-left" aria-hidden="true" />
        </button>
        <button type="button" class="sf-btn sf-btn--login" @click="goToLogin">Iniciar sesión</button>

        <img src="/assets/Logo.png" alt="Logo" class="sf-logo" />
        <h1 class="sf-title">Sistema de medición fotovoltaico</h1>
        <p class="sf-lead">
          Descripción del hardware, criterios de selección, plan de muestreo y variables medidas para el monitoreo del campo solar.
        </p>

        <div class="sf-body">
          <section class="sf-section">
            <h2>Plan de mediciones</h2>
            <p>
              De <strong>6:00 a 18:00</strong>, tomar aproximadamente <strong>30 muestras cada 2 segundos</strong> por hora (o más) durante <strong>12 horas</strong> al día, durante <strong>20 días</strong>.
            </p>
          </section>

          <section class="sf-section">
            <h2>Variables eléctricas críticas</h2>
            <p class="sf-note">Obligatorias para cumplir los objetivos del estudio.</p>
            <div class="sf-table-wrap">
              <table class="sf-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Símbolo</th>
                    <th>Utilidad</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Voltaje panel</td><td><em>V</em><sub>p</sub></td><td>Potencia</td></tr>
                  <tr><td>Corriente panel</td><td><em>I</em><sub>pv</sub></td><td>Potencia</td></tr>
                  <tr><td>Potencia</td><td><em>P</em></td><td>Rendimiento</td></tr>
                  <tr><td>Energía acumulada</td><td><em>E</em></td><td>Consumo / balance energético</td></tr>
                  <tr><td>Voltaje batería</td><td><em>V</em><sub>b</sub></td><td>Estado de batería</td></tr>
                  <tr><td>Corriente carga</td><td><em>I</em><sub>b</sub></td><td>Flujo energético</td></tr>
                  <tr><td>Estado de carga</td><td>SOC</td><td>Gestión energética</td></tr>
                </tbody>
              </table>
            </div>
            <p class="sf-metrics">
              A partir de lo anterior se pueden derivar: <strong>potencia instantánea</strong>, <strong>energía generada</strong>, <strong>eficiencia del sistema fotovoltaico</strong> e <strong>índice de pérdidas</strong>.
            </p>
          </section>

          <section class="sf-section">
            <h2>Variables ambientales</h2>
            <div class="sf-table-wrap">
              <table class="sf-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Símbolo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Irradiancia solar</td><td><em>G</em></td></tr>
                  <tr><td>Temperatura panel</td><td><em>T</em><sub>p</sub></td></tr>
                  <tr><td>Temperatura ambiente</td><td><em>T</em><sub>a</sub></td></tr>
                  <tr><td>Humedad relativa</td><td><em>H</em></td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="sf-section">
            <h2>Dinámica del sistema solar</h2>
            <p>
              Los paneles solares <strong>no cambian tan rápido</strong> el efecto de las variables. Las variaciones relevantes ocurren por: paso de nubes, temperatura, carga conectada, sombras y orientación solar.
            </p>
            <h3>Frecuencias recomendadas</h3>
            <div class="sf-table-wrap">
              <table class="sf-table">
                <thead>
                  <tr>
                    <th>Tipo de variable</th>
                    <th>Frecuencia recomendada</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Voltaje / corriente</td><td>Cada 2 s</td></tr>
                  <tr><td>Irradiancia</td><td>Cada 2 s</td></tr>
                  <tr><td>Temperatura</td><td>Cada 2 s</td></tr>
                  <tr><td>Energía acumulada</td><td>Cada 1 min</td></tr>
                  <tr><td>Transmisión LoRa</td><td>Cada 5 s, de cada uno de los datos, para no saturar el radio LoRa y evitar colisiones</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="sf-section">
            <h2>Hardware y justificación</h2>

            <article class="sf-card">
              <h3>1. Sensor de corriente ACS758-100</h3>
              <ul>
                <li>Sensor de efecto Hall para medir corriente continua (DC) o alterna (AC) hasta ±100 A.</li>
                <li>Salida analógica proporcional a la corriente.</li>
                <li>Baja pérdida de potencia (resistencia interna de 100 µΩ).</li>
                <li>Alimentación: 5 V / 3,3 V. Apto para control y monitorización de potencia.</li>
              </ul>
              <p><strong>Criterio de selección:</strong> se escogió según la corriente máxima de los paneles, con rango mayor que esa corriente para operar de forma segura en picos.</p>
            </article>

            <article class="sf-card">
              <h3>2. Sensor SHT10</h3>
              <ul>
                <li>Sensor digital de temperatura y humedad relativa.</li>
                <li>Comunicación I²C (2 hilos).</li>
                <li>Rango: temperatura −40 a 125 °C; humedad 0 a 100 % HR.</li>
                <li>Precisión: ±0,5 °C y ±4,5 % HR.</li>
                <li>Muy bajo consumo; adecuado para alimentación con batería.</li>
              </ul>
              <p><strong>Criterio de selección:</strong> resistencia a la intemperie y lectura de temperatura ambiente para contrastarla con la temperatura del panel.</p>
            </article>

            <article class="sf-card">
              <h3>3. Módulo XY-K485</h3>
              <ul>
                <li>Convertidor de nivel lógico TTL a RS-485.</li>
                <li>Comunicación serie a larga distancia (hasta ~1 km en condiciones adecuadas).</li>
                <li>Control automático de dirección de flujo (sin pin adicional dedicado).</li>
                <li>Velocidad hasta 256000 baudios.</li>
                <li>Alimentación: 3,3 a 30 V CC.</li>
              </ul>
              <p><strong>Uso:</strong> comunicación entre el módulo Heltec ESP LoRa y el sensor de voltaje con interfaz RS-485.</p>
            </article>

            <article class="sf-card">
              <h3>4. RTC DS3231</h3>
              <ul>
                <li>Reloj en tiempo real de alta precisión con oscilador compensado por temperatura (TCXO).</li>
                <li>Deriva típica menor a ±2 minutos por año.</li>
                <li>Bus I²C; batería de respaldo (CR2032).</li>
                <li>Alarma programable y salida de onda cuadrada.</li>
              </ul>
              <p><strong>Uso:</strong> que el módulo emisor conserve fecha y hora correctas y no se pierda el registro temporal de los datos ante cortes de energía o fallos, para poder constatar incidentes.</p>
            </article>

            <article class="sf-card">
              <h3>5. Fuente HLK-10M05</h3>
              <ul>
                <li>Fuente conmutada AC-DC compacta.</li>
                <li>Entrada: 85–265 V AC.</li>
                <li>Salida: 5 V CC / 2 A (10 W).</li>
                <li>Aislada, encapsulada, montaje tipo DIP.</li>
              </ul>
              <p><strong>Uso:</strong> alimentar el sistema desde la red eléctrica (microcontroladores y sensores auxiliares).</p>
            </article>

            <article class="sf-card">
              <h3>6. Peacefair PZEM-003-10A (voltímetro + amperímetro DC)</h3>
              <ul>
                <li>Medidor de energía en corriente continua.</li>
                <li>Mide voltaje (0–300 V), corriente (0–10 A), potencia y energía.</li>
                <li>RS-485 con protocolo Modbus-RTU.</li>
              </ul>
              <p><strong>Criterio de selección:</strong> medición del voltaje de los paneles con rango seguro respecto al voltaje máximo esperado.</p>
            </article>

            <article class="sf-card">
              <h3>7. Sensor DS18B20</h3>
              <ul>
                <li>Temperatura digital con bus 1-Wire.</li>
                <li>Rango: −55 a +125 °C; precisión ±0,5 °C (−10 a +85 °C).</li>
                <li>Dirección única por sensor (varios sensores en un mismo bus).</li>
                <li>Alimentación 3,0–5,5 V; modo parásito opcional.</li>
              </ul>
              <p><strong>Uso:</strong> medir la temperatura del panel solar.</p>
            </article>

            <article class="sf-card">
              <h3>8. Convertidor DC-DC elevador (Micro USB 2 A, SDB628, XY-016)</h3>
              <ul>
                <li>Entrada: 2–24 V; salida regulable (tensión de salida &gt; entrada; uso recomendado por debajo de ~26 V).</li>
                <li>Eficiencia declarada &gt; 93 %.</li>
                <li>Dimensiones aproximadas: 30 × 17 × 14 mm.</li>
                <li>Corriente de pico de salida no mayor a 2 A.</li>
              </ul>
              <p><strong>Uso:</strong> elevar la tensión disponible para entregar 5 V a componentes que lo requieran (por ejemplo el sensor de voltaje / módulo de medición).</p>
            </article>

            <article class="sf-card">
              <h3>9. Batería LiPo 4000 mAh</h3>
              <p>
                Si falta la conexión a red, el sistema puede seguir enviando información para detectar fallos o periodos sin generación.
              </p>
            </article>

            <article class="sf-card">
              <h3>10. Módulo microSD</h3>
              <p>
                Almacenamiento de respaldo en el módulo que transmite los datos, para comparar con lo recibido en destino, verificar pérdida de enlace con el receptor y auditar huecos de comunicación.
              </p>
            </article>

            <article class="sf-card sf-card--highlight">
              <h3>11. Heltec WiFi LoRa 32 (V3)</h3>
              <p>
                Placa de desarrollo todo-en-uno orientada a IoT: ESP32-S3 con radio LoRa de largo alcance, en formato compacto y de bajo consumo.
              </p>
              <h4>Microcontrolador principal (ESP32-S3FN8)</h4>
              <ul>
                <li>SoC de doble núcleo Xtensa 32 bits (LX7), hasta 240 MHz.</li>
                <li>Flash interna 8 MB (SiP); SRAM 512 KB.</li>
              </ul>
              <h4>Conectividad</h4>
              <ul>
                <li><strong>WiFi</strong> 802.11 b/g/n (antena de resorte 2,4 GHz).</li>
                <li><strong>Bluetooth</strong> 5.0 (BLE).</li>
                <li><strong>LoRa:</strong> chip Semtech SX1262; bandas 470–510 MHz o 863–928 MHz; TX máx. ~21 dBm; sensibilidad RX hasta −134 dBm (SF12, BW 125 kHz); conector IPEX/U.FL para antena externa.</li>
              </ul>
              <h4>Interfaces y periféricos</h4>
              <ul>
                <li>OLED 0,96″ (128×64).</li>
                <li>Cargador integrado para LiPo 3,7 V (conector SH1.25-2).</li>
                <li>USB tipo C (programación y alimentación).</li>
                <li>GPIO expandidos: ADC, touch, UART, I²C, SPI, etc.</li>
                <li>USB-serie CP2102.</li>
              </ul>
              <h4>Alimentación y consumo</h4>
              <ul>
                <li>Lógica a 3,3 V; alimentación por USB-C o batería LiPo.</li>
                <li>Modos de bajo consumo y deep sleep (&lt; 10 µA en condiciones de diseño), útil para instalaciones remotas.</li>
              </ul>
            </article>
          </section>

          <section class="sf-section">
            <h2>Diseño de circuitos</h2>
            <p>
              Para el diseño esquemático y PCB se utilizará <strong>EasyEDA</strong>: herramienta gratuita y abierta, con biblioteca de huellas y máscaras de componentes que agiliza el trazado y la documentación.
            </p>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const body = document.getElementsByTagName("body")[0];

export default {
  name: "SistemaFotovoltaico",
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push({ name: "Login" });
    },
    goToLogin() {
      this.$router.push({ name: "Login" });
    },
  },
  created() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    body.classList.add("bg-gray-100");
  },
};
</script>

<style scoped>
.sf-page {
  min-height: 100vh;
  font-family: Arial, Helvetica, sans-serif;
}

.sf-gradient {
  background: linear-gradient(to bottom, #706f6f, #4a4949, #2c2c2c);
  min-height: 100vh;
  padding: 20px;
}

.sf-panel {
  max-width: 920px;
  margin: 0 auto;
  padding: 24px 20px 40px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 15px;
  position: relative;
}

.sf-btn {
  position: absolute;
  background: none;
  border: none;
  color: #f9b000;
  cursor: pointer;
  font-size: 16px;
}

.sf-btn--back {
  top: 20px;
  left: 20px;
  font-size: 22px;
}

.sf-btn--login {
  top: 22px;
  right: 20px;
}

.sf-logo {
  width: 88px;
  height: 88px;
  object-fit: contain;
  margin: 48px auto 20px;
  display: block;
}

.sf-title {
  font-size: 26px;
  color: #f9b000;
  font-weight: bold;
  text-align: center;
  margin-bottom: 12px;
}

.sf-lead {
  color: rgba(255, 255, 255, 0.88);
  text-align: center;
  line-height: 1.5;
  margin: 0 auto 28px;
  max-width: 720px;
  font-size: 15px;
}

.sf-body {
  color: #fff;
}

.sf-section {
  margin-bottom: 32px;
}

.sf-section h2 {
  color: #f9b000;
  font-size: 20px;
  margin: 0 0 12px;
}

.sf-section h3 {
  color: #ffd45e;
  font-size: 17px;
  margin: 18px 0 8px;
}

.sf-section h4 {
  color: #ffe7a3;
  font-size: 15px;
  margin: 14px 0 6px;
}

.sf-section p,
.sf-section li {
  line-height: 1.6;
  margin-bottom: 10px;
}

.sf-section ul {
  margin: 0 0 12px 1.1rem;
  padding: 0;
}

.sf-note {
  opacity: 0.9;
  font-size: 14px;
  margin-top: -4px;
}

.sf-metrics {
  margin-top: 14px;
  font-size: 14px;
  opacity: 0.95;
}

.sf-table-wrap {
  overflow-x: auto;
  margin-top: 10px;
  border-radius: 8px;
}

.sf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.25);
}

.sf-table th,
.sf-table td {
  border: 1px solid rgba(249, 176, 0, 0.25);
  padding: 10px 12px;
  text-align: left;
  vertical-align: top;
}

.sf-table th {
  background: rgba(249, 176, 0, 0.15);
  color: #f9b000;
  font-weight: bold;
}

.sf-table tbody tr:nth-child(even) {
  background: rgba(255, 255, 255, 0.03);
}

.sf-card {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.sf-card--highlight {
  border-color: rgba(249, 176, 0, 0.35);
}

.sf-card h3 {
  margin-top: 0;
  color: #f9b000;
  font-size: 17px;
}

.sf-card ul {
  margin-bottom: 8px;
}
</style>
